import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { VoteIcon, SendHorizontalIcon as AdjustmentsHorizontalIcon, Compass } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getSnaps, deleteSnap } from '@/lib/api';
import { toggleLike } from '@/lib/api/likes';
import { castVote, removeVote } from '@/lib/api/votes';
import type { Snap, SnapCategory } from '@/types/database.types';
import { getActivityThresholds, getSnapActivity } from '@/utils/snap';
import DeleteModal from '@/components/DeleteModal';
import ActivityFilter from '@/components/filters/ActivityFilter';
import CategoryFilter from '@/components/filters/CategoryFilter';
import MarkedFilter from '@/components/filters/MarkedFilter';
import SnapCard from '@/components/SnapCard';
import Pagination from '@/components/Pagination';
import SearchBar from '@/components/SearchBar';
import Button from '@/components/form/Button';
import PendingInvites from '@/components/PendingInvites';
import RandomIconsBackground from '@/components/RandomIconsBackground';
import BackToTopButton from '@/components/BackToTopButton';

type ActivityFilter = 'all' | 'hot' | 'trending' | 'new' | 'system' | 'cash';

export default function Home() {
  const [snaps, setSnaps] = useState<Snap[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState<SnapCategory[]>([]);
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const [inviteOnlyFilter, setInviteOnlyFilter] = useState(false);
  const [snapToDelete, setSnapToDelete] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isVoting, setIsVoting] = useState<Record<string, boolean>>({});
  const ITEMS_PER_PAGE = 6;
  const navigate = useNavigate();
  const location = useLocation();

  const fetchSnaps = useCallback(async () => {
    try {
      console.log('Home: Fetching snaps...');
      setLoading(true);
      setFetchError(null);
      
      const response = await getSnaps(currentPage, ITEMS_PER_PAGE, searchQuery, userSearchQuery, inviteOnlyFilter);
      console.log('Home: Fetched snaps successfully:', response.data.length);
      
      setSnaps(response.data);
      setTotalPages(Math.ceil(response.total / ITEMS_PER_PAGE));
    } catch (error) {
      console.error('Failed to load SNAPs:', error);
      setFetchError('Failed to load SNAPs. Please try again later.');
      toast.error('Failed to load SNAPs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, userSearchQuery, inviteOnlyFilter]);

  // Initial data fetch when component mounts or when URL changes
  useEffect(() => {
    fetchSnaps();
  }, [fetchSnaps, location.pathname, location.key]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, userSearchQuery, inviteOnlyFilter]);

  const handleDelete = async (snapId: string) => {
    try {
      await deleteSnap(snapId);
      toast.success('SNAP deleted successfully');
      setSnaps(prev => prev.filter(snap => snap.id !== snapId));
    } catch (error) {
      toast.error('Failed to delete SNAP');
    }
  };

  const handleLike = async (snapId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      // Use the centralized like handling function
      const { liked, count } = await toggleLike(snapId);
      
      // Update the local state to reflect the like/unlike action
      setSnaps(prev => prev.map(snap => {
        if (snap.id === snapId) {
          return {
            ...snap,
            likes_count: count,
            userLiked: liked
          };
        }
        return snap;
      }));
      
      toast.success(liked ? 'SNAP liked' : 'SNAP unliked');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update like');
      }
    }
  };

  const handleVote = async (snapId: string, optionId: string) => {
    // Prevent multiple votes while a vote is in progress
    if (isVoting[snapId]) return;
    
    // Find the snap to check if user already voted for this option
    const snapToUpdate = snaps.find(s => s.id === snapId);
    if (!snapToUpdate) return;
    
    // Don't allow voting on the same option again
    if (snapToUpdate.userVote === optionId) {
      toast.error('You have already voted for this option');
      return;
    }
    
    try {
      // Set voting state for this snap
      setIsVoting(prev => ({ ...prev, [snapId]: true }));
      
      await castVote(snapId, optionId);
      
      // Refresh the snap data to ensure UI is in sync with database
      const updatedSnaps = await getSnaps(currentPage, ITEMS_PER_PAGE, searchQuery, userSearchQuery, inviteOnlyFilter);
      setSnaps(updatedSnaps.data);
      
      toast.success('Vote cast successfully!');
    } catch (error) {
      toast.error('Failed to cast vote');
    } finally {
      // Clear voting state for this snap after a short delay
      setTimeout(() => {
        setIsVoting(prev => ({ ...prev, [snapId]: false }));
      }, 500);
    }
  };

  const handleRemoveVote = async (snapId: string) => {
    // Prevent multiple operations while a vote is in progress
    if (isVoting[snapId]) return;
    
    try {
      // Set voting state for this snap
      setIsVoting(prev => ({ ...prev, [snapId]: true }));
      
      await removeVote(snapId);
      
      // Refresh the snap data to ensure UI is in sync with database
      const updatedSnaps = await getSnaps(currentPage, ITEMS_PER_PAGE, searchQuery, userSearchQuery, inviteOnlyFilter);
      setSnaps(updatedSnaps.data);
      
      toast.success('Vote removed successfully!');
    } catch (error) {
      toast.error('Failed to remove vote');
    } finally {
      // Clear voting state for this snap after a short delay
      setTimeout(() => {
        setIsVoting(prev => ({ ...prev, [snapId]: false }));
      }, 500);
    }
  };

  const thresholds = getActivityThresholds(snaps);

  const filteredSnaps = snaps.filter(snap => {
    // Category filter
    if (selectedCategories.length > 0) {
      const hasMatchingCategory = selectedCategories.some(category => 
        snap.categories.includes(category)
      );
      if (!hasMatchingCategory) return false;
    }

    // Activity filter
    const activity = getSnapActivity(snap, thresholds);
    switch (activityFilter) {
      case 'hot':
        return activity === 'hot';
      case 'trending':
        return activity === 'trending';
      case 'new':
        return activity === 'new';
      case 'system':
        return snap.is_system && !snap.cashsnap;
      case 'cash':
        return !!snap.cashsnap;
      default:
        return true;
    }
  });

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 -mt-8">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-200 to-blue-300 -mx-2 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-8 sm:py-12 mb-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-blue-900 mb-2">Dashboard</h1>
              <p className="text-blue-800">Discover and participate in community decisions</p>
            </div>
            <div className="flex flex-col xs:flex-row space-y-2 xs:space-y-0 xs:space-x-3">
              <Button
                onClick={() => navigate('/create')}
                variant="primary"
                icon={<VoteIcon className="h-5 w-5" />}
                className="w-full xs:w-auto lg:hidden"
              >
                Create SNAP
              </Button>
              <Button
                onClick={() => navigate('/feed')}
                variant="secondary"
                icon={<Compass className="h-5 w-5" />}
                className="w-full xs:w-auto"
              >
                SNAP Feed
              </Button>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-lg shadow-sm p-4 mt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <SearchBar
                onSearch={setSearchQuery}
                onUserSearch={setUserSearchQuery}
                placeholder="SNAPs or type @ for users..."
              />
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                icon={<AdjustmentsHorizontalIcon className="h-5 w-5" />}
              >
                Filters
              </Button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="mt-4 space-y-4 border-t pt-4">
                <ActivityFilter selected={activityFilter} onChange={setActivityFilter} />
                <CategoryFilter 
                  selectedCategories={selectedCategories} 
                  onChange={setSelectedCategories} 
                />
                <MarkedFilter value={inviteOnlyFilter} onChange={setInviteOnlyFilter} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pending Invites */}
      <PendingInvites />

      {/* Content Section with Background */}
      <div className="relative bg-white rounded-xl p-2 sm:p-8 mb-8">
        <RandomIconsBackground />
        <div className="relative z-10 grid grid-cols-1 gap-3 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            <div className="col-span-full flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading SNAPs...</p>
            </div>
          ) : fetchError ? (
            <div className="col-span-full bg-white rounded-lg shadow p-8 text-center">
              <VoteIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading SNAPs</h3>
              <p className="text-gray-600 mb-6">{fetchError}</p>
              <Button
                onClick={fetchSnaps}
                variant="primary"
              >
                Try Again
              </Button>
            </div>
          ) : filteredSnaps.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow p-8 text-center">
              <VoteIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No SNAPs found</h3>
              <p className="text-gray-600 mb-6">
                {searchQuery || userSearchQuery
                  ? 'No SNAPs match your search criteria.'
                  : selectedCategories.length > 0
                    ? `No SNAPs found in the selected ${selectedCategories.length === 1 ? 'category' : 'categories'}.`
                    : activityFilter === 'cash'
                      ? 'No CASHSNAPs available at the moment.'
                      : inviteOnlyFilter
                        ? 'No private SNAPs you have been invited to.'
                        : 'Create your first SNAP to get started!'}
              </p>
              <Button
                onClick={() => navigate('/create')}
                variant="primary"
                icon={<VoteIcon className="h-5 w-5" />}
              >
                Create SNAP
              </Button>
            </div>
          ) : (
            filteredSnaps.map((snap) => (
              <SnapCard
                key={snap.id}
                snap={{
                  ...snap,
                  activity: getSnapActivity(snap, thresholds)
                }}
                onDelete={(id) => setSnapToDelete(id)}
                onLike={handleLike}
                onVote={handleVote}
                onRemoveVote={handleRemoveVote}
                isVoting={isVoting[snap.id] || false}
              />
            ))
          )}
        </div>
      </div>

      <div className="mt-8 mb-12">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteModal
        isOpen={!!snapToDelete}
        onClose={() => setSnapToDelete(null)}
        onConfirm={() => {
          if (snapToDelete) handleDelete(snapToDelete);
        }}
        title="Delete SNAP"
        message="Are you sure you want to delete this SNAP? This action cannot be undone."
      />

      <BackToTopButton />
    </div>
  );
}